import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import buttonArrow from '../../assets/icons/button-arrow.svg';
import portfolioLogo from '../../assets/portfolio-logo.png';
import { sendContactEmail } from '../../utils/emailjs';
import './brand.css';

const sectors = [
  {
    name: 'Telecommunications',
    link: '/portfolio/telecommunication',
    description:
      'We invest in and operate premier telecommunications franchises, leveraging a robust retail footprint to create powerful economies of scale. By optimizing storefront operations and expanding consumer access points, we lower market barriers and increase connection velocity making us the ideal commercial partner for leading network providers looking to scale their retail reach.',
    className: 'kw__portfolio-card--telecommunications',
  },
  {
    name: 'Interior Design',
    link: '/portfolio/interior-design',
    description:
      'We provide comprehensive, turnkey interior design and spatial architecture solutions for high-end corporate, commercial, and retail environments. By merging creative design principles with advanced material sourcing and rigorous project management, we transform raw concepts into functional, inspiring environments.',
    className: 'kw__portfolio-card--interior',
  },
  {
    name: 'Real Estate & Construction',
    link: '/portfolio/real-estate',
    description:
      'We acquire, develop, and manage prime real estate assets, delivering high-quality residential, commercial, and mixed-use developments. By combining disciplined project management with strategic land acquisitions and robust construction standards, we build durable, high-value properties that drive urban development and offer sustainable long-term value for communities and investors alike.',
    className: 'kw__portfolio-card--real-estate',
  },
  {
    name: 'Automotive',
    link: '/portfolio/automotive',
    description:
      'We specialize in the strategic acquisition and retail management of premium passenger vehicles and in-demand automotive platforms. By leveraging strong regional dealer relationships, strict quality inspections, and volume buying power, we secure high-value automotive inventory to meet the evolving demands of individual buyers and corporate fleets.',
    className: 'kw__portfolio-card--automotive',
  },
  {
    name: 'Business Process Outsourcing (BPO) & Global Operations',
    link: '/portfolio/bpo',
    description:
      'We provide high-performance BPO solutions that empower enterprises to streamline back-office workflows, optimize overheads, and scale with absolute confidence. By merging advanced management frameworks with top-tier talent, we manage data integrity, multi-channel support, and cross-border service levels with zero operational lag.',
    className: 'kw__portfolio-card--bpo',
  },
  {
    name: 'Fashion & Apparel',
    link: '/portfolio/fashion-apparel',
    description:
      'Fashion brands scale when creative vision is directly integrated with operational execution. Moving an apparel line from concept through production to distribution requires aligning product development, supply chain logistics, and multi-channel retail strategies to turn artistic ideas into a sustainable business.',
    className: 'kw__portfolio-card--fashion',
  },
  {
    name: 'Food & Hospitality',
    link: '/portfolio/food-hospitality',
    description:
      'We build, acquire, and manage high-performance restaurant concepts and food brands that resonate with modern consumers. By combining exceptional culinary execution with robust supply chain management and standardized kitchen operations, we transform dining spaces into highly profitable, memorable consumer destinations.',
    className: 'kw__portfolio-card--food',
  },
  {
    name: 'Global Trade, Import & Export Logistics',
    link: '/portfolio/import-export',
    description:
      'We manage a robust import and export portfolio, serving as a trusted gateway for global commerce. Combining deep regulatory expertise, extensive distribution networks, and agile logistics management, we streamline cross-border supply chains ensuring critical commodities and commercial goods reach their destinations efficiently.',
    className: 'kw__portfolio-card--trade',
  },
  {
    name: 'Philanthropy & Community Impact',
    link: '/portfolio/philanthropy',
    description:
      'Through our dedicated philanthropic arm, the JB Foundation, we channel resources directly into high-impact social initiatives. We approach philanthropy with strict operational accountability, prioritizing access to quality healthcare, creating robust educational opportunities, and supporting community development programs to foster a more resilient, equitable future.',
    className: 'kw__portfolio-card--philanthropy',
  },
];

const Brand = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    introduction: '',
  });

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactForm((current) => ({ ...current, [name]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    await sendContactEmail(contactForm, 'Portfolio');
    setContactForm({
      name: '',
      email: '',
      introduction: '',
    });
  };

  return (
    <div className="kw__portfolio-page" id="brand">
      <section className="kw__portfolio-hero kw__portfolio-section">
        <h1 className="kw__portfolio-title">Driving Scale, Stability, and Excellence Across Diverse Industries</h1>
        <p className="kw__portfolio-copy">
          At Kothawala Group, we build, acquire, and manage high-performance businesses across a wide spectrum of essential global industries. Our
          strength lies in our ability to apply disciplined operational frameworks, institutional capital, and strategic management to every sector
          we touch. From connecting communities through retail telecommunications to engineering inspiring corporate spaces, we turn market
          potential into sustained commercial excellence.
        </p>
      </section>

      <section className="kw__portfolio-industries">
        <div className="kw__portfolio-grid kw__portfolio-section">
          {sectors.map((sector) => (
            <article key={sector.name} className={`kw__portfolio-card ${sector.className}`}>
              <h2>{sector.name}</h2>
              <p>{sector.description}</p>
              <Link to={sector.link} className="kw__portfolio-readmore">
                <span>Read more</span>
                <span className="kw__portfolio-readmore-icon">
                  <img src={buttonArrow} alt="" />
                </span>
              </Link>
            </article>
          ))}

          <div className="kw__portfolio-brandblock">
            <img src={portfolioLogo} alt="Kothawala Group" />
          </div>
        </div>
      </section>

      <section className="kw__portfolio-contact kw__portfolio-section">
        <div className="kw__portfolio-contact-copy">
          <p className="kw__portfolio-contact-label">Connect With Our Team</p>
          <h2>Partner With Us</h2>
          <p>
            Our diversified portfolio is built for global brand partners, investors, and forward-thinking enterprises who understand that commercial
            scale only succeeds when strong back-end operations can perform reliably under sustained market pressure. Whether you are looking to
            scale your retail footprint, optimize your corporate operations, or collaborate on a strategic joint venture, let&apos;s build the future
            together.
          </p>
        </div>

        <form className="kw__portfolio-contact-form" onSubmit={handleContactSubmit}>
          <div className="kw__portfolio-contact-row">
            <input name="name" placeholder="Name" value={contactForm.name} onChange={handleContactChange} />
            <input name="email" placeholder="Email address" value={contactForm.email} onChange={handleContactChange} />
          </div>
          <textarea
            name="introduction"
            placeholder="Introduction"
            value={contactForm.introduction}
            onChange={handleContactChange}
          />
          <button type="submit" className="kw__portfolio-contact-submit">
            <span>Submit</span>
            <span className="kw__portfolio-readmore-icon">
              <img src={buttonArrow} alt="" />
            </span>
          </button>
        </form>
      </section>
    </div>
  );
};

export default Brand;
